import { useState } from 'react';
import { toast } from 'sonner';
import { PickerChip, ReasonTable } from '@/components/kit';
import type { ReasonRow } from '@/components/kit';
import { monthName } from '../lib/useAccounts';

/**
 * Add an adjustment — `g_m_adj` (7c). A manual line on a statement has to say
 * why: the reason table is the picker, and the reason (plus the note) is what
 * travels to the statement beside the figure. Shared by the run list and the
 * statement, which both offer the verb.
 */
const REASONS: ReasonRow[] = [
  {
    key: 'correction',
    label: 'Correction from the previous run',
    consequence: 'adds a dated line to this statement, and names the run it corrects',
  },
  {
    key: 'advance',
    label: 'Advance repayment',
    consequence: 'reduces the running advance balance by the same amount',
  },
  {
    key: 'deduction',
    label: 'Agreed deduction — consumables or an external bill',
    consequence: 'posts against the record it names, never a bare number',
  },
  {
    key: 'addition',
    label: 'Agreed addition — bonus or arrears',
    consequence: 'adds to gross; it does not change the base',
  },
  {
    key: 'dispute',
    label: 'Disputed line returned by the person',
    consequence: 'reopens the line with their note attached, for the next run',
  },
];

export function AdjustModal({
  open,
  who,
  period,
  onClose,
  onSave,
}: {
  open: boolean;
  who: string;
  period: string;
  onClose: () => void;
  onSave: (kind: 'deduction' | 'addition', amountMinor: number, reason: string) => void;
}) {
  const [kind, setKind] = useState<'deduction' | 'addition'>('deduction');
  const [value, setValue] = useState('');

  return (
    <ReasonTable
      open={open}
      onClose={onClose}
      title="Add an adjustment"
      sub={`${who} · ${monthName(period)} — every adjustment names its reason; a slip with an unexplained number starts an argument`}
      reasons={REASONS}
      notePlaceholder="Reason required — the adjustment is logged with your name and the time."
      confirmLabel="Add the adjustment"
      before={
        <div className="flex" style={{ gap: 12, alignItems: 'flex-end' }}>
          <div>
            <p className="t-ovl mut">Which way</p>
            <p className="mt4">
              <PickerChip
                label={`${kind === 'deduction' ? 'Deduction' : 'Addition'} ▾`}
                ariaLabel="Kind"
                kind="brand"
                value={kind}
                onChange={setKind}
                options={[
                  { value: 'deduction' as const, label: 'Deduction', hint: 'comes off what they are owed' },
                  { value: 'addition' as const, label: 'Addition', hint: 'goes on top' },
                ]}
              />
            </p>
          </div>
          <div style={{ flex: 1 }}>
            <label className="t-ovl mut" htmlFor="adj-amt">Amount</label>
            <input
              id="adj-amt"
              className="fld t-strong mt4 num"
              inputMode="decimal"
              placeholder="0.00"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
          </div>
        </div>
      }
      onConfirm={(reason, note) => {
        const minor = Math.round(Number(value) * 100);
        if (!(minor > 0)) {
          toast.info('Put the amount in — it is what comes on or off the figure.');
          return;
        }
        const label = reason.key === 'other' ? note : note ? `${reason.label} — ${note}` : reason.label;
        onSave(reason.key === 'addition' ? 'addition' : kind, minor, label);
        setValue('');
      }}
    />
  );
}
